import {
  FocalEventPosterImage,
  getCoverEventPosterImage,
} from '@/features/events/shared/FocalEventPosterImage';
import { AppScreen, Card, ScreenHeader } from '@/components/ui/scene';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import type {
  DiscoveryEvent,
  ProfileSummary,
  VenueDealCategory,
  VenuePublicDeal,
  VenueSummary,
} from '@/lib/api/apiTypes';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { getVenueDealCategoryLabel } from '@radious/catalog/deal-categories';
import { useAuth } from '@/lib/auth/auth';
import { formatDealDays, formatDealTimeRange, getDealTiming } from '@/features/venues/deals/deals';
import { APP_FEATURES } from '@/lib/app/features';
import {
  createLoadTimer,
  trackDealSearchAppearance,
  trackDealTap,
  trackEmptyStateSeen,
  trackEventSearchAppearance,
  trackSearchQuery,
  trackSearchResultTapped,
  trackVenueSearchAppearance,
} from '@/lib/analytics/posthog';
import { createSearchCorrelationID } from '@/lib/analytics/searchAnalyticsPrivacy';
import { formatEventDayLabel, formatEventTime } from '@/features/events/eventFormatting';
import { getProfileInitials } from '@/features/profile/profileFormatting';
import { getEventHref, getEventPath, getPublicIdentityHref, getVenueHref, getVenuePath } from '@/lib/navigation/routes';
import { addSentryActionBreadcrumb, createSentryInputTracker } from '@/lib/analytics/sentry';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { useUserOriginCoords } from '@/lib/location/locationOrigin';
import { formatApproximateFeedDistanceLabel } from '@/features/events/feed/feedFormatters';
import { useMapEvents } from '@/features/map/useMapEvents';
import { useFeedPreferences } from '@/features/events/feed/feedPreferences';
import {
  buildSmartSearchResults,
  type SmartSearchResult,
} from '@/features/search/smartSearchResults';
import { SmartSearchResultRow } from '@/features/search/SmartSearchResultRow';
import { type RecentSearchEntry, useRecentSearches } from '@/features/search/useRecentSearches';
import { useSearch } from '@/features/search/useSearch';
import { cn } from '@/lib/utils';
import { getVenueCategoryConfig, resolveVenueCategory } from '@/features/venues/venueCategories';
import {
  getResolvedFeedEntryBadgeLabel,
  shouldShowPrimaryTicketCTA,
} from '@/features/events/eventMobileHelpers';
import { Redirect, router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import {
  ArrowLeftIcon,
  BadgeCheckIcon,
  Building2Icon,
  CalendarDaysIcon,
  ChevronRightIcon,
  ClockIcon,
  MapPinIcon,
  SearchIcon,
  TicketPercent,
  XIcon,
} from 'lucide-react-native';
import * as React from 'react';
import {
  Image,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type SearchFilter = 'all' | 'events' | 'deals' | 'venues' | 'people';

const SEARCH_FILTERS: { value: SearchFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'events', label: 'Events' },
  { value: 'deals', label: 'Deals' },
  { value: 'venues', label: 'Venues' },
  { value: 'people', label: 'Profiles' },
];

const SEARCH_SUGGESTIONS = ['Tonight', 'Live music', 'Free', 'Happy hour', 'DJ'];
const SEARCH_EMPTY_SUGGESTIONS = ['Tonight', 'Live music', 'Free', 'Happy hour'];
const SEARCH_ANALYTICS_IDLE_MS = 750;
const SEARCH_APPEARANCE_LIMIT = 5;
const EARTH_RADIUS_METERS = 6371000;

function getRouteQueryParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
}

function SearchSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <View className="gap-3">
      <View className="flex-row items-center justify-between gap-3">
        <Text variant="small" className="uppercase tracking-[2px] text-muted-foreground">
          {title}
        </Text>
        {action}
      </View>
      <View className="gap-3">{children}</View>
    </View>
  );
}

function buildRecentEvent(event: DiscoveryEvent): RecentSearchEntry {
  const posterImage = getCoverEventPosterImage(event);
  return {
    key: `event:${event.id}`,
    type: 'event',
    title: event.title,
    subtitle: event.venue_name || 'Venue TBD',
    image_url: posterImage?.public_url ?? null,
    poster_image: posterImage,
    route: getEventPath(event.id, event.title) as RecentSearchEntry['route'],
  };
}

function buildRecentProfile(profile: ProfileSummary): RecentSearchEntry {
  return {
    key: `profile:${profile.user_id}`,
    type: 'profile',
    title: profile.display_name || profile.username,
    subtitle: `@${profile.username}`,
    image_url: profile.avatar_url || null,
    route:
      profile.is_venue && profile.venue_id
        ? (getVenuePath(profile.venue_id, {
            name: profile.venue_name,
          }) as RecentSearchEntry['route'])
        : `/profiles/${profile.username}`,
  };
}

function buildRecentVenue(venue: VenueSummary): RecentSearchEntry {
  return {
    key: `venue:${venue.id}`,
    type: 'venue',
    title: venue.name,
    subtitle: venue.address || 'Venue',
    image_url: venue.logo_url || null,
    route: getVenuePath(venue.id, { name: venue.name }) as RecentSearchEntry['route'],
  };
}

function buildRecentDeal(deal: VenuePublicDeal): RecentSearchEntry {
  return {
    key: `deal:${deal.id}`,
    type: 'deal',
    title: deal.title,
    subtitle: deal.venue_name,
    image_url: deal.logo_url || null,
    route: getVenuePath(deal.venue_id, { name: deal.venue_name }) as RecentSearchEntry['route'],
  };
}

function buildRecentQuery(query: string): RecentSearchEntry {
  return {
    key: `query:${query.toLowerCase()}`,
    type: 'query',
    title: query,
    subtitle: 'Search query',
    query,
  };
}

function getDealCategoryLabel(category: VenueDealCategory) {
  return getVenueDealCategoryLabel(category);
}

function getEventTimingLabel(event: DiscoveryEvent) {
  const now = Date.now();
  const startsAt = new Date(event.starts_at).getTime();
  const endsAt = event.ends_at ? new Date(event.ends_at).getTime() : startsAt + 4 * 60 * 60 * 1000;
  const diffMinutes = Math.max(0, Math.round((startsAt - now) / 60000));

  if (now >= startsAt && now < endsAt) {
    return 'Happening now';
  }

  if (diffMinutes <= 60) {
    return `Starts in ${diffMinutes}m`;
  }

  return `${formatEventDayLabel(event.starts_at)} · ${formatEventTime(event.starts_at)}`;
}

function getEventSignals(event: DiscoveryEvent) {
  const entryLabel = getResolvedFeedEntryBadgeLabel(event);
  return [
    event.viewer_rsvp_status === 'going' ? 'Going' : null,
    event.is_saved ? 'Saved' : null,
    entryLabel,
    shouldShowPrimaryTicketCTA(event.entry_type, event.ticket_url) ? 'Tickets' : null,
  ].filter((signal): signal is string => Boolean(signal));
}

function getVenueDistanceMeters(
  venueLatitude: number | undefined,
  venueLongitude: number | undefined,
  originLatitude: number,
  originLongitude: number
) {
  if (
    typeof venueLatitude !== 'number' ||
    typeof venueLongitude !== 'number' ||
    !Number.isFinite(originLatitude) ||
    !Number.isFinite(originLongitude)
  ) {
    return null;
  }

  const dLat = ((venueLatitude - originLatitude) * Math.PI) / 180;
  const dLng = ((venueLongitude - originLongitude) * Math.PI) / 180;
  const lat1 = (originLatitude * Math.PI) / 180;
  const lat2 = (venueLatitude * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * EARTH_RADIUS_METERS * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function DealSearchRow({
  deal,
  position,
  query,
  onPress,
}: {
  deal: VenuePublicDeal;
  position: number;
  query: string;
  onPress: () => void;
}) {
  const timing = getDealTiming(deal);
  const isLive = timing.state === 'live';
  const categoryLabel = getDealCategoryLabel(deal.category);
  const statusLabel = isLive ? 'Live now' : timing.label;
  const railLabel = isLive ? 'LIVE' : 'DEAL';
  const description = String(deal.description ?? '').trim();

  return (
    <Pressable
      onPress={onPress}
      className={cn(
        'overflow-hidden rounded-2xl border bg-surface-alt',
        isLive ? 'border-status-soon' : 'border-border'
      )}>
      <View className="flex-row">
        <View
          className={cn(
            'w-16 items-center justify-center gap-2 px-2 py-4',
            isLive ? 'bg-status-soon' : 'bg-primary-soft'
          )}>
          <View
            className={cn(
              'size-9 items-center justify-center rounded-full border',
              isLive ? 'border-background/60' : 'border-primary-edge'
            )}>
            <Icon
              as={TicketPercent}
              size={20}
              className={isLive ? 'text-background' : 'text-primary'}
            />
          </View>
          <Text
            variant="small"
            className={cn(
              'text-[11px] font-semibold',
              isLive ? 'text-background' : 'text-primary'
            )}>
            {railLabel}
          </Text>
        </View>

        <View className="flex-1 gap-3 p-4">
          <View className="flex-row items-start justify-between gap-3">
            <View className="gap-1">
              <Text variant="small" className="font-semibold text-status-soon">
                {statusLabel}
              </Text>
              <Text variant="small" className="text-muted-foreground">
                {categoryLabel}
              </Text>
            </View>
            {query.length >= 2 ? (
              <Text variant="small" className="text-muted-foreground">
                #{position}
              </Text>
            ) : null}
          </View>

          <View className="gap-2">
            <Text className="text-xl font-semibold leading-6 text-foreground">{deal.title}</Text>
            <View className="flex-row items-center gap-2">
              <Icon as={MapPinIcon} size={14} color={DESIGN_TOKENS.colors.textMuted} />
              <Text variant="muted" className="flex-1 leading-5">
                {deal.venue_name}
              </Text>
            </View>
          </View>

          <View className="gap-2 border-t border-border pt-3">
            <View className="flex-row items-center gap-2">
              <Icon as={ClockIcon} size={14} color={DESIGN_TOKENS.colors.textMuted} />
              <Text variant="small" className="font-medium text-muted-foreground">
                {formatDealTimeRange(deal)}
              </Text>
            </View>
            <View className="flex-row items-center gap-2">
              <Icon as={CalendarDaysIcon} size={14} color={DESIGN_TOKENS.colors.textMuted} />
              <Text variant="small" className="font-medium text-muted-foreground">
                {formatDealDays(deal.active_days)}
              </Text>
            </View>
          </View>

          {description ? (
            <Text variant="small" className="leading-5 text-muted-foreground" numberOfLines={2}>
              {description}
            </Text>
          ) : null}
        </View>
      </View>
    </Pressable>
  );
}

function EventSearchRow({
  event,
  position,
  query,
  onPress,
}: {
  event: DiscoveryEvent;
  position: number;
  query: string;
  onPress: () => void;
}) {
  const timingLabel = getEventTimingLabel(event);
  const isSoon = timingLabel === 'Happening now' || timingLabel.startsWith('Starts in');
  const signals = getEventSignals(event);
  const posterImage = getCoverEventPosterImage(event);

  return (
    <Pressable
      onPress={onPress}
      className="overflow-hidden rounded-2xl border border-border bg-surface-alt">
      <View className="flex-row items-stretch">
        <View className="flex-1 gap-2 p-4">
          <View className="flex-row items-center gap-2">
            <Text
              variant="small"
              className={cn(isSoon ? 'font-semibold text-status-soon' : 'text-muted-foreground')}>
              {timingLabel}
            </Text>
            {query.length >= 2 ? (
              <Text variant="small" className="text-muted-foreground">
                #{position}
              </Text>
            ) : null}
          </View>
          <Text className="text-lg font-semibold leading-6 text-foreground">{event.title}</Text>
          <View className="flex-row items-center gap-2">
            <Icon as={MapPinIcon} size={14} color={DESIGN_TOKENS.colors.textMuted} />
            <Text variant="muted" className="flex-1 leading-5">
              {event.venue_name || 'Venue TBD'}
            </Text>
          </View>
          {signals.length > 0 ? (
            <View className="flex-row flex-wrap gap-2">
              {signals.map((signal) => {
                const isGoing = signal === 'Going';
                const isSaved = signal === 'Saved';
                const isTickets = signal === 'Tickets';
                return (
                  <View
                    key={signal}
                    className={cn(
                      'rounded-md border px-2 py-1',
                      isGoing
                        ? 'border-status-soon/40 bg-status-soon/10'
                        : isSaved || isTickets
                          ? 'border-primary-edge bg-primary-soft'
                          : 'border-border'
                    )}>
                    <Text
                      variant="small"
                      className={cn(
                        'font-medium',
                        isGoing
                          ? 'text-status-soon'
                          : isSaved || isTickets
                            ? 'text-primary'
                            : 'text-muted-foreground'
                      )}>
                      {signal}
                    </Text>
                  </View>
                );
              })}
            </View>
          ) : null}
        </View>
        {posterImage ? (
          <FocalEventPosterImage image={posterImage} className="w-24 bg-canvas-inset" />
        ) : null}
      </View>
    </Pressable>
  );
}

function FilterButton({
  filter,
  active,
  onPress,
}: {
  filter: { value: SearchFilter; label: string };
  active: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      className={cn(
        'rounded-lg border px-3 py-2',
        active ? 'border-primary-edge bg-primary-soft' : 'border-border bg-surface-alt'
      )}>
      <Text
        variant="small"
        className={cn(active ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
        {filter.label}
      </Text>
    </Pressable>
  );
}

function RecentSearchRow({
  entry,
  isLast,
  onPress,
  onDismiss,
}: {
  entry: RecentSearchEntry;
  isLast: boolean;
  onPress: () => void;
  onDismiss: () => void;
}) {
  const eventPosterImage =
    entry.type === 'event'
      ? (entry.poster_image ?? (entry.image_url ? { public_url: entry.image_url } : null))
      : null;

  return (
    <View
      className="flex-row items-center gap-3 p-4"
      style={
        !isLast
          ? { borderBottomWidth: 1, borderBottomColor: DESIGN_TOKENS.colors.border }
          : undefined
      }>
      <Pressable onPress={onPress} className="flex-1 flex-row items-center gap-4">
        {eventPosterImage ? (
          <FocalEventPosterImage
            image={eventPosterImage}
            style={{
              width: 52,
              height: 52,
              borderRadius: entry.type === 'event' ? 18 : 26,
              backgroundColor: DESIGN_TOKENS.colors.canvasInset,
            }}
          />
        ) : entry.image_url ? (
          <Image
            source={{ uri: entry.image_url }}
            style={{
              width: 52,
              height: 52,
              borderRadius: 26,
              backgroundColor: DESIGN_TOKENS.colors.canvasInset,
            }}
          />
        ) : entry.type === 'profile' ? (
          <View
            className="items-center justify-center rounded-full bg-primary-soft"
            style={{ width: 52, height: 52 }}>
            <Text className="text-base font-semibold text-foreground">
              {getProfileInitials(entry.title)}
            </Text>
          </View>
        ) : (
          <View
            className="items-center justify-center rounded-[18px] bg-primary-soft"
            style={{ width: 52, height: 52 }}>
            <Icon
              as={
                entry.type === 'event'
                  ? CalendarDaysIcon
                  : entry.type === 'deal'
                    ? TicketPercent
                    : entry.type === 'query'
                      ? SearchIcon
                      : Building2Icon
              }
              size={20}
              color={DESIGN_TOKENS.colors.accentStrong}
            />
          </View>
        )}
        <View className="flex-1 gap-1">
          <Text className="text-base font-semibold text-foreground">{entry.title}</Text>
          {entry.subtitle ? (
            <Text variant="muted" className="leading-5">
              {entry.subtitle}
            </Text>
          ) : null}
        </View>
      </Pressable>
      <Pressable
        accessibilityLabel={`Remove ${entry.title} from recent searches`}
        hitSlop={10}
        onPress={onDismiss}
        className="size-10 items-center justify-center rounded-full bg-canvas-inset">
        <Icon as={XIcon} size={16} color={DESIGN_TOKENS.colors.textMuted} />
      </Pressable>
    </View>
  );
}

export default function SearchScreen() {
  const params = useLocalSearchParams<{ q?: string | string[] }>();
  const isDesktop = useIsDesktop();
  const insets = useSafeAreaInsets();
  const session = useAuth((state) => state.session);
  const userOrigin = useUserOriginCoords();
  const initialQuery = React.useMemo(() => getRouteQueryParam(params.q), [params.q]);
  const setFeedTab = useFeedPreferences((state) => state.setFeedTab);
  const setTimeWindow = useFeedPreferences((state) => state.setTimeWindow);
  const [query, setQuery] = React.useState(initialQuery);
  const [filterState, setFilterState] = React.useState<{
    query: string;
    value: SearchFilter;
  }>(() => ({ query: initialQuery.trim(), value: 'all' }));
  const searchQuery = useSearch(query, APP_FEATURES.search, 12);
  const soonEventsQuery = useMapEvents({
    enabled: APP_FEATURES.search && query.trim().length < 2,
    latitude: userOrigin.latitude,
    limit: 6,
    longitude: userOrigin.longitude,
    originLatitude: userOrigin.latitude,
    originLongitude: userOrigin.longitude,
    sort: 'soonest',
    timeWindow: 'live',
  });
  const { recentSearches, saveSearch, clearSearches, removeSearch, isHydrated } =
    useRecentSearches();
  const userCounty = session?.me.profile?.last_county_name ?? null;
  const loadTimerRef = React.useRef(createLoadTimer('search'));
  const searchSessionKeyRef = React.useRef(`${Date.now()}:${Math.random().toString(36).slice(2)}`);
  const searchInputRef = React.useRef<TextInput | null>(null);
  const appearedSearchResultsRef = React.useRef(new Set<string>());
  const searchInputTracker = React.useMemo(
    () => createSentryInputTracker({ screen: 'search', field: 'query', form: 'global_search' }),
    []
  );

  const trimmedQuery = query.trim();
  const searchCorrelationID = React.useMemo(createSearchCorrelationID, [trimmedQuery]);
  const activeFilter = filterState.query === trimmedQuery ? filterState.value : 'all';
  const updateActiveFilter = React.useCallback(
    (value: SearchFilter) => setFilterState({ query: trimmedQuery, value }),
    [trimmedQuery]
  );
  const events = React.useMemo(() => searchQuery.data?.events ?? [], [searchQuery.data?.events]);
  const deals = React.useMemo(() => searchQuery.data?.deals ?? [], [searchQuery.data?.deals]);
  const soonEvents = React.useMemo(
    () => soonEventsQuery.data?.events ?? [],
    [soonEventsQuery.data?.events]
  );

  const profiles = React.useMemo(
    () => (searchQuery.data?.profiles ?? []).filter((profile) => !profile.is_venue),
    [searchQuery.data?.profiles]
  );
  const venues = React.useMemo(() => searchQuery.data?.venues ?? [], [searchQuery.data?.venues]);
  const visibleEvents = activeFilter === 'all' || activeFilter === 'events' ? events : [];
  const visibleDeals = activeFilter === 'all' || activeFilter === 'deals' ? deals : [];
  const visibleProfiles = activeFilter === 'all' || activeFilter === 'people' ? profiles : [];
  const visibleVenues = activeFilter === 'all' || activeFilter === 'venues' ? venues : [];
  const topResults = React.useMemo(
    () =>
      buildSmartSearchResults({
        deals,
        events,
        profiles,
        query: trimmedQuery,
        venues,
      }),
    [deals, events, profiles, trimmedQuery, venues]
  );
  const hasResults =
    events.length > 0 || deals.length > 0 || profiles.length > 0 || venues.length > 0;
  const hasVisibleResults =
    visibleEvents.length > 0 ||
    visibleDeals.length > 0 ||
    visibleProfiles.length > 0 ||
    visibleVenues.length > 0;
  const hasRecentSearches = recentSearches.length > 0;
  const bottomPadding = isDesktop ? 32 : Math.max(insets.bottom + 128, 156);
  const searchErrorCopy = React.useMemo(
    () =>
      getAPIErrorRecoveryCopy(
        searchQuery.error,
        undefined,
        'Search is having trouble right now. Please try again in a moment.'
      ),
    [searchQuery.error]
  );

  useFocusEffect(
    React.useCallback(() => {
      searchInputRef.current?.focus();
    }, [])
  );

  React.useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  React.useEffect(() => {
    loadTimerRef.current = createLoadTimer('search');
  }, [trimmedQuery]);

  React.useEffect(() => {
    if (trimmedQuery.length < 2 || searchQuery.isPending) {
      return;
    }

    loadTimerRef.current.done();

    if (searchQuery.isError) {
      const timeout = setTimeout(() => {
        trackEmptyStateSeen({
          screen: 'search',
          userCounty,
          reason: 'error',
        });
      }, SEARCH_ANALYTICS_IDLE_MS);
      return () => clearTimeout(timeout);
    }

    const timeout = setTimeout(() => {
      trackSearchQuery({
        query: trimmedQuery,
        resultsCount: events.length + deals.length + profiles.length + venues.length,
        searchCorrelationID,
        userCounty,
        sessionKey: searchSessionKeyRef.current,
      });

      events.slice(0, SEARCH_APPEARANCE_LIMIT).forEach((event, index) => {
        const key = `${searchCorrelationID}:event:${event.id}:${index}`;
        if (appearedSearchResultsRef.current.has(key)) {
          return;
        }
        appearedSearchResultsRef.current.add(key);
        trackEventSearchAppearance({
          eventID: event.id,
          position: index + 1,
          query: trimmedQuery,
          searchCorrelationID,
          sessionKey: searchSessionKeyRef.current,
          venueID: event.venue_id ?? null,
        });
      });
      deals.slice(0, SEARCH_APPEARANCE_LIMIT).forEach((deal, index) => {
        const key = `${searchCorrelationID}:deal:${deal.id}:${index}`;
        if (appearedSearchResultsRef.current.has(key)) {
          return;
        }
        appearedSearchResultsRef.current.add(key);
        trackDealSearchAppearance({
          dealID: deal.id,
          position: index + 1,
          query: trimmedQuery,
          searchCorrelationID,
          sessionKey: searchSessionKeyRef.current,
          venueID: deal.venue_id,
        });
      });
      venues.slice(0, SEARCH_APPEARANCE_LIMIT).forEach((venue, index) => {
        const key = `${searchCorrelationID}:venue:${venue.id}:${index}`;
        if (appearedSearchResultsRef.current.has(key)) {
          return;
        }
        appearedSearchResultsRef.current.add(key);
        trackVenueSearchAppearance({
          position: index + 1,
          query: trimmedQuery,
          searchCorrelationID,
          sessionKey: searchSessionKeyRef.current,
          venueID: venue.id,
        });
      });

      if (!hasResults) {
        trackEmptyStateSeen({
          screen: 'search',
          userCounty,
          reason: 'no_results',
        });
      }
    }, SEARCH_ANALYTICS_IDLE_MS);

    return () => clearTimeout(timeout);
  }, [
    deals.length,
    deals,
    events.length,
    events,
    hasResults,
    profiles.length,
    searchQuery.isError,
    searchQuery.isPending,
    searchCorrelationID,
    trimmedQuery,
    userCounty,
    venues.length,
    venues,
  ]);

  function openRecentSearch(entry: RecentSearchEntry) {
    addSentryActionBreadcrumb({
      screen: 'search',
      action: 'open_recent_search',
      data: {
        entry_type: entry.type,
        has_route: Boolean(entry.route),
        has_query: Boolean(entry.query),
      },
    });
    void saveSearch(entry);

    if (entry.route) {
      router.navigate(entry.route);
      return;
    }

    if (entry.query) {
      setQuery(entry.query);
    }
  }

  function runSearchQuery(nextQuery: string) {
    const nextTrimmedQuery = nextQuery.trim();
    if (!nextTrimmedQuery) {
      return;
    }

    setQuery(nextTrimmedQuery);
    void saveSearch(buildRecentQuery(nextTrimmedQuery));
  }

  function openNearbyEvents() {
    setFeedTab('nearby');
    setTimeWindow('today');
    Keyboard.dismiss();
    router.navigate('/(tabs)/events');
  }

  function saveCurrentQuery() {
    if (trimmedQuery.length >= 2) {
      void saveSearch(buildRecentQuery(trimmedQuery));
    }
  }

  function openEventResult(event: DiscoveryEvent, position: number) {
    addSentryActionBreadcrumb({
      screen: 'search',
      action: 'open_event_result',
      data: {
        query_length: trimmedQuery.length,
        event_id: event.id,
        position,
      },
    });
    trackSearchResultTapped({
      query: trimmedQuery,
      position,
      eventID: event.id,
      searchCorrelationID,
      targetType: 'event',
      venueID: event.venue_id ?? null,
    });
    saveCurrentQuery();
    void saveSearch(buildRecentEvent(event));
    Keyboard.dismiss();
    router.navigate(getEventHref(event.id, event.title) as never);
  }

  function openDealResult(deal: VenuePublicDeal, position: number) {
    addSentryActionBreadcrumb({
      screen: 'search',
      action: 'open_deal_result',
      data: {
        query_length: trimmedQuery.length,
        deal_id: deal.id,
        venue_id: deal.venue_id,
        position,
      },
    });
    trackSearchResultTapped({
      dealID: deal.id,
      query: trimmedQuery,
      position,
      searchCorrelationID,
      targetType: 'deal',
      venueID: deal.venue_id,
    });
    trackDealTap({
      dealID: deal.id,
      position,
      source: 'search',
      surface: 'mobile_search',
      venueID: deal.venue_id,
    });
    saveCurrentQuery();
    void saveSearch(buildRecentDeal(deal));
    Keyboard.dismiss();
    router.navigate(
      getVenueHref(deal.venue_id, { deal: deal.id, source: 'search' }, { name: deal.venue_name })
    );
  }

  function openVenueResult(venue: VenueSummary, position: number) {
    addSentryActionBreadcrumb({
      screen: 'search',
      action: 'open_venue_result',
      data: {
        query_length: trimmedQuery.length,
        venue_id: venue.id,
        position,
      },
    });
    trackSearchResultTapped({
      query: trimmedQuery,
      position,
      searchCorrelationID,
      targetType: 'venue',
      venueID: venue.id,
    });
    saveCurrentQuery();
    void saveSearch(buildRecentVenue(venue));
    Keyboard.dismiss();
    router.navigate(getVenueHref(venue.id, { source: 'search' }, { name: venue.name }));
  }

  function openProfileResult(profile: ProfileSummary, position: number) {
    addSentryActionBreadcrumb({
      screen: 'search',
      action: 'open_profile_result',
      data: {
        query_length: trimmedQuery.length,
        profile_id: profile.user_id,
        position,
      },
    });
    trackSearchResultTapped({
      query: trimmedQuery,
      position,
      searchCorrelationID,
      targetType: 'profile',
      venueID: profile.venue_id ?? null,
    });
    saveCurrentQuery();
    void saveSearch(buildRecentProfile(profile));
    Keyboard.dismiss();
    router.navigate(getPublicIdentityHref(profile));
  }

  function openTopResult(result: SmartSearchResult, index: number) {
    const position = index + 1;

    switch (result.type) {
      case 'event':
        openEventResult(result.item, position);
        return;
      case 'deal':
        openDealResult(result.item, position);
        return;
      case 'profile':
        openProfileResult(result.item, position);
        return;
      case 'venue':
        openVenueResult(result.item, position);
        return;
    }
  }

  if (!APP_FEATURES.search) {
    return <Redirect href="/(tabs)/events" />;
  }

  return (
    <AppScreen edges={[]}>
      <KeyboardAvoidingView className="flex-1" behavior={undefined}>
        <ScrollView
          automaticallyAdjustKeyboardInsets
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingTop: insets.top + 12,
            paddingBottom: bottomPadding,
            gap: 18,
          }}
          keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
          keyboardShouldPersistTaps="handled"
          onScrollBeginDrag={() => Keyboard.dismiss()}>
          <ScreenHeader
            title="Search"
            subtitle="Find events, deals, venues, and public profiles."
            action={
              <Pressable
                accessibilityLabel="Back"
                onPress={() => router.back()}
                className="size-12 items-center justify-center rounded-full border border-border bg-surface-alt">
                <Icon as={ArrowLeftIcon} size={20} color={DESIGN_TOKENS.colors.text} />
              </Pressable>
            }
          />

          <Pressable
            accessibilityRole="search"
            onPress={() => searchInputRef.current?.focus()}
            className="rounded-[22px] border border-border bg-surface-alt px-4 py-3 active:opacity-90">
            <View className="flex-row items-center gap-3">
              <Icon as={SearchIcon} size={18} color={DESIGN_TOKENS.colors.textMuted} />
              <TextInput
                ref={searchInputRef}
                autoCapitalize="none"
                autoCorrect={false}
                clearButtonMode="while-editing"
                onChangeText={(value) => {
                  searchInputTracker.changeText(value);
                  setQuery(value);
                }}
                onFocus={() => searchInputTracker.focus()}
                onBlur={() => searchInputTracker.blur(query)}
                onSubmitEditing={() => {
                  searchInputTracker.submit(query);
                  if (trimmedQuery.length >= 2) {
                    void saveSearch(buildRecentQuery(trimmedQuery));
                  }
                  Keyboard.dismiss();
                }}
                placeholder="Search events, deals, profiles, or venues"
                placeholderTextColor={DESIGN_TOKENS.colors.textMuted}
                returnKeyType="search"
                style={{
                  flex: 1,
                  color: DESIGN_TOKENS.colors.text,
                  fontSize: 16,
                  paddingVertical: 2,
                }}
                value={query}
              />
            </View>
          </Pressable>

          {trimmedQuery.length >= 2 ? (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerClassName="gap-2">
              {SEARCH_FILTERS.map((filter) => (
                <FilterButton
                  key={filter.value}
                  filter={filter}
                  active={activeFilter === filter.value}
                  onPress={() => updateActiveFilter(filter.value)}
                />
              ))}
            </ScrollView>
          ) : null}

          {trimmedQuery.length < 2 ? (
            <SearchSection title="Try These">
              <View className="flex-row flex-wrap gap-2">
                {SEARCH_SUGGESTIONS.map((suggestion) => (
                  <Pressable
                    key={suggestion}
                    onPress={() => runSearchQuery(suggestion)}
                    className="rounded-lg border border-border bg-surface-alt px-3 py-2">
                    <Text variant="small" className="font-medium text-foreground">
                      {suggestion}
                    </Text>
                  </Pressable>
                ))}
                <Pressable
                  onPress={openNearbyEvents}
                  className="rounded-lg border border-primary-edge bg-primary-soft px-3 py-2">
                  <Text variant="small" className="font-semibold text-foreground">
                    Near me
                  </Text>
                </Pressable>
              </View>
            </SearchSection>
          ) : null}

          {trimmedQuery.length < 2 && soonEvents.length > 0 ? (
            <SearchSection title="Happening Soon">
              {soonEvents.map((event, index) => (
                <EventSearchRow
                  key={event.id}
                  event={event}
                  position={index + 1}
                  query=""
                  onPress={() => openEventResult(event, index + 1)}
                />
              ))}
            </SearchSection>
          ) : null}

          {trimmedQuery.length < 2 && !isHydrated ? (
            <SearchSection title="Recent Searches">
              <Card className="overflow-hidden rounded-[24px] border-border bg-surface-alt p-0">
                {[0, 1, 2].map((i) => (
                  <View
                    key={i}
                    className="flex-row items-center gap-4 p-4"
                    style={
                      i < 2
                        ? { borderBottomWidth: 1, borderBottomColor: DESIGN_TOKENS.colors.border }
                        : undefined
                    }>
                    <Skeleton className="size-[52px] rounded-[18px]" />
                    <View className="flex-1 gap-2">
                      <Skeleton className="h-4 w-3/4 rounded" />
                      <Skeleton className="h-3 w-1/2 rounded" />
                    </View>
                  </View>
                ))}
              </Card>
            </SearchSection>
          ) : null}

          {trimmedQuery.length < 2 && isHydrated && hasRecentSearches ? (
            <SearchSection
              title="Recent Searches"
              action={
                <Pressable onPress={() => void clearSearches()}>
                  <Text variant="small" className="font-semibold text-primary">
                    Clear
                  </Text>
                </Pressable>
              }>
              <Card className="rounded-[24px] border-border bg-surface-alt p-0">
                {recentSearches.map((entry, index) => (
                  <RecentSearchRow
                    key={entry.key}
                    entry={entry}
                    isLast={index === recentSearches.length - 1}
                    onPress={() => openRecentSearch(entry)}
                    onDismiss={() => void removeSearch(entry.key)}
                  />
                ))}
              </Card>
            </SearchSection>
          ) : null}

          {trimmedQuery.length < 2 && isHydrated && !hasRecentSearches ? (
            <View className="items-center gap-4 px-4 py-8">
              <View className="size-16 items-center justify-center rounded-full bg-canvas-inset">
                <Icon as={ClockIcon} size={28} className="text-muted-foreground" />
              </View>
              <View className="items-center gap-1.5">
                <Text className="text-base font-semibold text-foreground">No recent searches</Text>
                <Text variant="muted" className="text-center leading-6">
                  Events, deals, venues, and profiles you search for will appear here.
                </Text>
              </View>
            </View>
          ) : null}

          {trimmedQuery.length >= 2 && searchQuery.isPending ? (
            <Card className="rounded-[24px] border-border bg-surface-alt p-5">
              <Text className="text-lg font-semibold text-foreground">Searching…</Text>
              <Text variant="muted" className="mt-2 leading-6">
                Looking across upcoming events, deals, public profiles, and venues.
              </Text>
            </Card>
          ) : null}

          {trimmedQuery.length >= 2 && searchQuery.isError ? (
            <Card className="rounded-[24px] border-destructive/40 bg-surface-alt p-5">
              <Text className="text-lg font-semibold text-foreground">Search unavailable</Text>
              <Text variant="muted" className="mt-2 leading-6">{searchErrorCopy}</Text>
              <View className="mt-4 flex-row flex-wrap gap-2">
                <Pressable
                  onPress={() => void searchQuery.refetch()}
                  className="rounded-lg border border-primary-edge bg-primary-soft px-3 py-2">
                  <Text variant="small" className="font-semibold text-foreground">
                    Try again
                  </Text>
                </Pressable>
                <Pressable
                  onPress={openNearbyEvents}
                  className="rounded-lg border border-border bg-canvas-inset px-3 py-2">
                  <Text variant="small" className="font-medium text-foreground">
                    Near me
                  </Text>
                </Pressable>
              </View>
            </Card>
          ) : null}

          {trimmedQuery.length >= 2 &&
          !searchQuery.isPending &&
          !searchQuery.isError &&
          !hasVisibleResults ? (
            <Card className="rounded-[24px] border-border bg-surface-alt p-5">
              <Text className="text-lg font-semibold text-foreground">No matches yet</Text>
              <Text variant="muted" className="mt-2 leading-6">
                {hasResults
                  ? 'No matches in this filter. Try All or switch to another result type.'
                  : 'Try a venue, event, deal, profile, or one of these searches.'}
              </Text>
              <View className="mt-4 flex-row flex-wrap gap-2">
                {SEARCH_EMPTY_SUGGESTIONS.map((suggestion) => (
                  <Pressable
                    key={suggestion}
                    onPress={() => runSearchQuery(suggestion)}
                    className="rounded-lg border border-border bg-canvas-inset px-3 py-2">
                    <Text variant="small" className="font-medium text-foreground">
                      {suggestion}
                    </Text>
                  </Pressable>
                ))}
                <Pressable
                  onPress={openNearbyEvents}
                  className="rounded-lg border border-primary-edge bg-primary-soft px-3 py-2">
                  <Text variant="small" className="font-semibold text-foreground">
                    Near me
                  </Text>
                </Pressable>
              </View>
            </Card>
          ) : null}

          {activeFilter === 'all' && topResults.length > 0 ? (
            <SearchSection title="Top Results">
              {topResults.map((result, index) => (
                <SmartSearchResultRow
                  key={result.key}
                  result={result}
                  onPress={() => openTopResult(result, index)}
                />
              ))}
            </SearchSection>
          ) : null}

          {visibleEvents.length > 0 ? (
            <SearchSection title="Events">
              {visibleEvents.map((event, index) => (
                <EventSearchRow
                  key={event.id}
                  event={event}
                  position={index + 1}
                  query={trimmedQuery}
                  onPress={() => openEventResult(event, index + 1)}
                />
              ))}
            </SearchSection>
          ) : null}

          {visibleDeals.length > 0 ? (
            <SearchSection title="Deals">
              {visibleDeals.map((deal, index) => (
                <DealSearchRow
                  key={deal.id}
                  deal={deal}
                  position={index + 1}
                  query={trimmedQuery}
                  onPress={() => openDealResult(deal, index + 1)}
                />
              ))}
            </SearchSection>
          ) : null}

          {visibleProfiles.length > 0 ? (
            <SearchSection title="Profiles">
              {visibleProfiles.map((profile, index) => (
                <Pressable
                  key={profile.user_id}
                  onPress={() => openProfileResult(profile, index + 1)}
                  className="flex-row items-center gap-4 rounded-[24px] border border-border bg-surface-alt p-4">
                  <Avatar
                    alt={profile.display_name || profile.username}
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 26,
                      backgroundColor: DESIGN_TOKENS.colors.canvasInset,
                    }}>
                    {profile.avatar_url ? (
                      <AvatarImage
                        source={{ uri: profile.avatar_url }}
                        style={{ width: 52, height: 52 }}
                      />
                    ) : null}
                    <AvatarFallback className="bg-primary-soft">
                      <Text className="text-base font-semibold text-foreground">
                        {getProfileInitials(profile.display_name || profile.username)}
                      </Text>
                    </AvatarFallback>
                  </Avatar>
                  <View className="flex-1">
                    <Text className="text-base font-semibold text-foreground">
                      {profile.display_name || profile.username}
                    </Text>
                    <Text variant="muted" className="mt-1">
                      @{profile.username}
                    </Text>
                    {profile.is_venue && profile.venue_name ? (
                      <Text variant="muted" className="mt-1">
                        Venue: {profile.venue_name}
                      </Text>
                    ) : null}
                    {profile.is_limited ? (
                      <Text variant="small" className="mt-1 text-muted-foreground">
                        Friends only
                      </Text>
                    ) : null}
                  </View>
                  <Icon as={ChevronRightIcon} size={18} className="text-muted-foreground" />
                </Pressable>
              ))}
            </SearchSection>
          ) : null}

          {visibleVenues.length > 0 ? (
            <SearchSection title="Venues">
              {visibleVenues.map((venue, index) => {
                const category = resolveVenueCategory(venue.category);
                const categoryConfig = getVenueCategoryConfig(category);
                const CategoryIcon = categoryConfig.icon;
                const distanceMeters = getVenueDistanceMeters(
                  venue.lat,
                  venue.lng,
                  userOrigin.latitude,
                  userOrigin.longitude
                );
                const distanceLabel =
                  distanceMeters == null
                    ? null
                    : formatApproximateFeedDistanceLabel(distanceMeters, userOrigin.isApproximate);
                const avatarBg = '#' + categoryConfig.bgColor;
                const avatarColor = '#' + categoryConfig.color;
                return (
                  <Pressable
                    key={venue.id}
                    onPress={() => openVenueResult(venue, index + 1)}
                    className="flex-row items-center gap-3 rounded-[20px] border border-border bg-surface-alt px-4 py-3.5">
                    <View className="relative shrink-0">
                      {venue.logo_url ? (
                        <View
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: 24,
                            borderWidth: 2,
                            borderColor: avatarColor + '55',
                            overflow: 'hidden',
                            backgroundColor: avatarBg,
                          }}>
                          <Image
                            source={{ uri: venue.logo_url }}
                            style={{ width: 44, height: 44 }}
                            accessibilityLabel={venue.name}
                          />
                        </View>
                      ) : (
                        <View
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: 24,
                            backgroundColor: avatarBg,
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          accessibilityLabel={venue.name}>
                          <Icon as={CategoryIcon} size={20} style={{ color: avatarColor }} />
                        </View>
                      )}
                      {venue.is_verified ? (
                        <View className="absolute -bottom-0.5 -right-0.5 size-[18px] items-center justify-center rounded-full border-2 border-surface-alt bg-background">
                          <Icon as={BadgeCheckIcon} size={11} className="text-primary" />
                        </View>
                      ) : null}
                    </View>
                    <View className="min-w-0 flex-1">
                      <Text
                        className="text-base font-semibold leading-5 text-foreground"
                        numberOfLines={1}>
                        {venue.name}
                      </Text>
                      <Text
                        variant="small"
                        className="mt-0.5 text-muted-foreground"
                        numberOfLines={1}>
                        {categoryConfig.label}
                        {venue.address ? ` · ${venue.address}` : ''}
                      </Text>
                    </View>
                    {distanceLabel ? (
                      <Text variant="small" className="shrink-0 text-muted-foreground">
                        {distanceLabel}
                      </Text>
                    ) : null}
                  </Pressable>
                );
              })}
            </SearchSection>
          ) : null}
        </ScrollView>
      </KeyboardAvoidingView>
    </AppScreen>
  );
}

export function ErrorBoundary({ retry }: { error: Error; retry: () => void }) {
  return (
    <AppScreen>
      <View className="flex-1 px-4 py-6">
        <ScreenHeader
          title="Search"
          subtitle="Find events, deals, venues, and public profiles."
          action={
            <Pressable
              accessibilityLabel="Back"
              onPress={() => router.back()}
              className="size-12 items-center justify-center rounded-full border border-border bg-surface-alt">
              <Icon as={ArrowLeftIcon} size={20} color={DESIGN_TOKENS.colors.text} />
            </Pressable>
          }
        />
        <Card className="mt-6 rounded-[24px] border-border bg-surface-alt p-5">
          <Text className="text-lg font-semibold text-foreground">Search hit a snag</Text>
          <Text variant="muted" className="mt-2 leading-6">
            Something got tangled while loading search. Try again, or head back and reopen search.
          </Text>
          <Pressable
            onPress={retry}
            className="mt-5 items-center rounded-full bg-primary px-4 py-3">
            <Text className="font-semibold text-primary-foreground">Try again</Text>
          </Pressable>
        </Card>
      </View>
    </AppScreen>
  );
}
